import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import StarRating from "@/components/ratings/StarRating";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Shown in both candidate & producer dashboards: lists missions whose end date has
 * passed and where the current user hasn't rated their counterpart yet. */
export default function PendingRatingsTab() {
  const { t } = useTranslation();
  const [pending, setPending] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submittingId, setSubmittingId] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/ratings/pending").then(({ data }) => setPending(data));
  useEffect(() => { load(); }, []);

  const handleSubmit = async (applicationId) => {
    if (stars < 1) return;
    setSubmittingId(applicationId);
    try {
      await api.post("/ratings", { application_id: applicationId, stars, comment });
      toast.success(t("ratings.submitted"));
      setOpenId(null);
      setStars(0);
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSubmittingId(null);
    }
  };

  if (!pending) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>;

  return (
    <div data-testid="pending-ratings-tab">
      {pending.length === 0 ? (
        <p className="text-center py-16 text-[#2B2B2B]/50" data-testid="pending-ratings-empty-message">{t("ratings.noPending")}</p>
      ) : (
        <div className="space-y-4">
          {pending.map((p) => (
            <div key={p.application_id} className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`pending-rating-row-${p.application_id}`}>
              <div>
                <p className="font-semibold">{p.counterpart_name}</p>
                <p className="text-xs text-[#2B2B2B]/50">{p.offer_title}</p>
              </div>
              <Dialog open={openId === p.application_id} onOpenChange={(o) => { setOpenId(o ? p.application_id : null); setStars(0); setComment(""); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-lg bg-[#4ba3b7] hover:bg-[#3D8A9A]" data-testid={`open-rate-dialog-${p.application_id}`}>
                    {t("ratings.rateNow")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader>
                    <DialogTitle>{t("ratings.rateTitle", { name: p.counterpart_name })}</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center gap-4 py-4">
                    <StarRating value={stars} onChange={setStars} size="h-8 w-8" testId={`rate-dialog-stars-${p.application_id}`} />
                    <Textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={t("ratings.commentPlaceholder")}
                      className="rounded-xl"
                      data-testid={`rate-dialog-comment-${p.application_id}`}
                    />
                  </div>
                  <DialogFooter>
                    <Button onClick={() => handleSubmit(p.application_id)} disabled={stars < 1 || submittingId === p.application_id}
                            className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid={`submit-rating-${p.application_id}`}>
                      {submittingId === p.application_id ? t("common.loading") : t("ratings.submit")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
